import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquare, PictureInPicture2 } from 'lucide-react'
import { MriButton, cn } from '@mriqbox/ui-kit'
import { VoicePlayback, type VoiceTrack } from '@/components/VoicePlayback'
import { useVoiceLevels } from '@/hooks/useVoiceLevels'
import { useDocumentPip, videoPipSupported } from '@/hooks/useDocumentPip'
import { useDockAutoHide } from '@/hooks/useDockAutoHide'
import { useRoomSounds } from '@/hooks/useRoomSounds'
import { canModerate, sameChannel, type RoomState, type ShareKind } from '@shared/protocol'
import { streamKey } from '@/hooks/useRoomSession'
import { ControlDock } from '@/components/ControlDock'
import { AvailableCards, AvailableStrip } from '@/components/AvailableStreams'
import { EmptyStage } from '@/components/EmptyStage'
import { usePageAway } from '@/hooks/usePageAway'
import { ChatPanel } from '@/components/ChatPanel'
import { RoomEditor, type RoomEditorValues } from '@/components/RoomEditor'
import { patchPermanentRoom } from '@/lib/permanentRooms'
import { RoomSidebar } from '@/components/RoomSidebar'
import { RoomTopbar } from '@/components/RoomTopbar'
import { Stage, type StreamMeasures, type ViewMode } from '@/components/Stage'
import { qualityLabel, type StreamMeasure } from '@/lib/streamQuality'
import { StreamTile, type StageStream } from '@/components/StreamTile'
import { useFullscreen } from '@/hooks/useFullscreen'
import type { RoomSession } from '@/hooks/useRoomSession'
import { cameraSupported, screenShareSupported } from '@/lib/screenCapture'
import { encoderStatus } from '@/lib/videoEncoder'

export interface RoomPageProps {
    session: RoomSession
    room: RoomState
}

function mensagemDeErro(error: string | undefined): string {
    switch (error) {
        case 'login_required': return 'Entre com o Discord de novo pra editar esta sala.'
        case 'not_found': return 'Esta sala nao e sua (ou nao existe mais).'
        case 'invalid_name': return 'Escolha um nome pra sala.'
        case 'invalid_pin': return 'O PIN precisa ter de 4 a 12 caracteres.'
        case 'room_in_use': return 'Tem gente na sala — o endereco so muda com ela vazia.'
        case 'code_taken': return 'Ja existe uma sala nesse endereco.'
        case 'code_must_match_name': return 'O endereco tem que ser o do nome da sala.'
        default: return 'Nao consegui salvar. Tente de novo.'
    }
}

const CHAT_ABERTO_KEY = 'mri_chat_aberto'

/** Aberto por padrao: quem nunca escolheu deve ao menos saber que existe. */
function lerChatAberto(): boolean {
    try {
        return localStorage.getItem(CHAT_ABERTO_KEY) !== '0'
    } catch {
        return true
    }
}

export function RoomPage({ session, room }: RoomPageProps) {
    const [mode, setMode] = useState<ViewMode>('mosaico')
    const [focusedId, setFocusedId] = useState<string | null>(null)
    /** Volume por pessoa (0 a 1) e o "silenciar tudo". Vale so nesta aba. */
    const [volumes, setVolumes] = useState<Record<string, number>>({})
    const [deafened, setDeafened] = useState(false)
    /**
     * Som ligado e volume de cada TRANSMISSAO (chave `pessoa:tipo`).
     *
     * Mora aqui, e nao no tile, porque o tile e remontado quando o id do
     * participante muda — o que acontece a cada reconexao em que a retomada
     * nao pega. Guardada la dentro, a escolha voltava ao padrao e a
     * transmissao "mutava sozinha" pra quem estava assistindo.
     */
    /**
     * Transmissoes que ESTE espectador escolheu nao ver (chave `pessoa:tipo`).
     *
     * Nao e so esconder: o tile sai do DOM, e com ele o `<video>`. No modo
     * mesh o quadro chega de qualquer jeito — o publicador manda uma copia por
     * espectador —, mas decodificar e desenhar sao a parte cara, e e ela que
     * some. Com cinco telas no palco, ver so uma corta o custo de GPU em quase
     * tudo.
     */
    /** My own previews I turned off. */
    const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set())
    /** Other people's shares this viewer chose to watch; nothing else is received. */
    const [watchedIds, setWatchedIds] = useState<Set<string>>(new Set())
    const pageAway = usePageAway()
    const [tileAudioOn, setTileAudioOn] = useState<Record<string, boolean>>({})
    const [tileVolumes, setTileVolumes] = useState<Record<string, number>>({})
    /**
     * Bate-papo aberto? A escolha fica gravada nesta maquina: quem transmite
     * costuma querer a tela inteira pro video, e reabrir o painel a cada
     * entrada seria brigar com a pessoa toda vez.
     */
    const [chatOpen, setChatOpen] = useState(() => lerChatAberto())
    /** Quantas chegaram com o painel fechado — vira o numerinho no botao. */
    const [naoLidas, setNaoLidas] = useState(0)
    /**
     * Painel da sala aberto como gaveta — so no celular.
     *
     * No desktop ele e coluna fixa e este estado nao muda nada. Sem a gaveta,
     * quem entra pelo telefone nao via a lista de participantes, nem o volume
     * de cada um, nem o codigo da sala pra convidar alguem: tudo isso mora la.
     */
    const [painelAberto, setPainelAberto] = useState(false)
    /** Formulario de edicao da sala fixa, aberto pelo dono sem sair dela. */
    const [editando, setEditando] = useState(false)
    const [erroEdicao, setErroEdicao] = useState<string | null>(null)
    const shellRef = useRef<HTMLDivElement>(null)
    const stageRef = useRef<HTMLDivElement>(null)
    /** A area do palco — e o passar do mouse por ela que traz a barra de volta. */
    const palcoRef = useRef<HTMLElement>(null)
    const { isFullscreen, toggle: toggleFullscreen } = useFullscreen(shellRef)
    const pip = useDocumentPip()
    /**
     * Qual transmissao esta na janela flutuante. `null` = o palco inteiro,
     * que e o comportamento do botao da barra de cima.
     */
    const [pipTargetId, setPipTargetId] = useState<string | null>(null)

    const { selfId, self, streams, captures, sharingKinds, selfChannel } = session
    /**
     * Sala fixa e quem esta olhando e o dono dela: so ai faz sentido oferecer a
     * edicao. `host` nao serve — e um cargo de dentro da sala, que passa
     * adiante quando alguem sai.
     */
    const podeEditarSala = !!self?.owner && !!room.name
    const canShare = useMemo(() => screenShareSupported(), [])
    const canUseCamera = useMemo(() => cameraSupported(), [])
    /** O que esta codificando o video que sai daqui, pros dois menus de ajuste. */
    const encoderNote = encoderStatus(
        session.encoder,
        session.encoderInUse,
        session.gpuAvailable
    )

    useEffect(() => {
        try {
            localStorage.setItem(CHAT_ABERTO_KEY, chatOpen ? '1' : '0')
        } catch {
            // Aba anonima ou storage bloqueado: a preferencia vale so nesta sessao.
        }
    }, [chatOpen])

    const totalMensagens = session.messages.length
    const vistasRef = useRef(totalMensagens)
    const versaoVistaRef = useRef(session.historyVersion)

    useEffect(() => {
        // Historico que chega (ao entrar, ou ao trocar de sub-sala) e o que ja
        // tinha sido dito, nao novidade: entra como visto. Sem isto, trocar de
        // sub-sala com o painel fechado contava a diferenca de tamanho entre
        // duas conversas que nao tem nada a ver uma com a outra.
        if (versaoVistaRef.current !== session.historyVersion) {
            versaoVistaRef.current = session.historyVersion
            vistasRef.current = totalMensagens
        }
        // Com o painel aberto a pessoa esta vendo tudo; fechado, acumula.
        if (chatOpen) {
            vistasRef.current = totalMensagens
            setNaoLidas(0)
            return
        }
        setNaoLidas(Math.max(0, totalMensagens - vistasRef.current))
    }, [chatOpen, totalMensagens, session.historyVersion])

    /**
     * Quem esta na mesma sub-sala que eu (eu incluso).
     *
     * E so com essas pessoas que voz, tela e camera trafegam — o palco, o
     * audio e os avisos sonoros partem daqui. A lista de participantes segue
     * mostrando a sala inteira: e por ela que se ve quem esta em cada sub-sala.
     */
    const mesmaSala = useMemo(
        () => room.participants.filter((participant) => sameChannel(participant, { channel: selfChannel ?? undefined })),
        [room.participants, selfChannel]
    )
    const canalAtual = room.channels?.find((channel) => channel.id === selfChannel)

    /**
     * Aviso se dispensa sozinho; erro fica ate alguem clicar.
     *
     * O texto da tela sem som e longo de proposito (explica onde fica a
     * caixinha do navegador), e um paragrafo desse tamanho parado sobre o
     * palco atrapalha justamente quem acabou de comecar a transmitir. Quinze
     * segundos e tempo de ler; o que fica no lugar e a marca "sem som" no
     * proprio tile, que nao tem prazo.
     */
    const { error: erroDaSessao, clearError } = session
    useEffect(() => {
        if (erroDaSessao?.severity !== 'aviso') return
        const timer = setTimeout(clearError, 15_000)
        return () => clearTimeout(timer)
    }, [erroDaSessao, clearError])

    /**
     * Um tile por TRANSMISSAO, nao por pessoa: quem esta com tela e camera no
     * ar aparece duas vezes no palco, e cada uma pode ser destacada sozinha.
     */
    const stageStreams = useMemo<StageStream[]>(() => mesmaSala.flatMap((participant) => {
        const isSelf = participant.id === selfId
        const base = {
            peerId: participant.id,
            nickname: participant.nickname,
            avatar: participant.avatar,
            isSelf,
            isHost: participant.host
        }

        // As proprias capturas vem do estado local, que muda antes de o
        // servidor confirmar — senao o proprio tile demoraria a aparecer.
        if (isSelf) {
            // Voz nao tem imagem: entra na lista de participantes, nunca no palco.
            return captures.filter((capture) => capture.kind !== 'voice').map((capture) => ({
                ...base,
                id: `${participant.id}:${capture.kind}`,
                kind: capture.kind,
                stream: capture.stream,
                watchers: session.watcherCounts[capture.kind]
            }))
        }

        return participant.publications.filter((publication) => publication.kind !== 'voice').map((publication) => {
            let stream: MediaStream | null = null
            if (publication.streamId) {
                stream = streams[streamKey(participant.id, publication.streamId)] ?? null
            }
            // Only without a stream id: otherwise this would show their screen on the camera tile I just opened.
            if (!stream && !publication.streamId) {
                const prefix = `${participant.id}::`
                const peerKey = Object.keys(streams).find((k) => k.startsWith(prefix) && (streams[k]?.getVideoTracks().length ?? 0) > 0)
                if (peerKey) stream = streams[peerKey] ?? null
            }

            return {
                ...base,
                id: `${participant.id}:${publication.kind}`,
                kind: publication.kind,
                // `null` enquanto a conexao ainda esta sendo montada: o tile ja
                // aparece com o aviso de "conectando".
                stream
            }
        })
    }), [mesmaSala, selfId, captures, streams, session.watcherCounts])

    // Filtered here, not with CSS: the point is removing the <video>. See docs/assistir-sob-demanda.md.
    const visibleStreams = useMemo(
        () => stageStreams.filter((entry) => (entry.isSelf ? !hiddenIds.has(entry.id) : watchedIds.has(entry.id))),
        [stageStreams, hiddenIds, watchedIds]
    )

    const availableStreams = useMemo(
        () => stageStreams.filter((entry) => (entry.isSelf ? hiddenIds.has(entry.id) : !watchedIds.has(entry.id))),
        [stageStreams, hiddenIds, watchedIds]
    )

    const toggleWatch = useCallback((id: string) => {
        const toggle = (atual: Set<string>) => {
            const proximo = new Set(atual)
            if (proximo.has(id)) proximo.delete(id)
            else proximo.add(id)
            return proximo
        }
        if (id.startsWith(`${selfId}:`)) setHiddenIds(toggle)
        else setWatchedIds(toggle)
    }, [selfId])

    const watchAll = useCallback(() => {
        setWatchedIds((atual) => new Set([...atual, ...availableStreams.filter((entry) => !entry.isSelf).map((entry) => entry.id)]))
    }, [availableStreams])

    // A share that ended takes the choice with it: starting again asks for a new click.
    useEffect(() => {
        const vivos = new Set(stageStreams.map((entry) => entry.id))
        const limpar = (atual: Set<string>) => {
            const proximo = new Set([...atual].filter((id) => vivos.has(id)))
            return proximo.size === atual.size ? atual : proximo
        }
        setHiddenIds(limpar)
        setWatchedIds(limpar)
    }, [stageStreams])

    /**
     * A barra de controles so se esconde quando ha transmissao no ar e o palco
     * esta aqui: com a janela flutuante aberta, o que sobra nesta tela e um
     * aviso, e nao ha imagem pra barra atrapalhar.
     */
    const dockVisivel = useDockAutoHide(palcoRef, visibleStreams.length > 0 && !pip.pipWindow)

    /** Audio de quem esta na voz — de todo mundo menos voce. */
    const voiceTracks = useMemo<VoiceTrack[]>(() => mesmaSala.flatMap((participant) => {
        if (participant.id === selfId) return []
        const voice = participant.publications.find((publication) => publication.kind === 'voice')
        if (!voice) return []

        let stream: MediaStream | null = null
        if (voice.streamId) {
            stream = streams[streamKey(participant.id, voice.streamId)] ?? null
        }
        if (!stream) {
            const prefix = `${participant.id}::`
            const peerKey = Object.keys(streams).find((k) => k.startsWith(prefix) && (streams[k]?.getAudioTracks().length ?? 0) > 0)
            if (peerKey) stream = streams[peerKey] ?? null
        }

        return stream ? [{ peerId: participant.id, stream }] : []
    }), [mesmaSala, selfId, streams])

    /*
     * Os avisos sonoros: alguem entrando ou saindo da voz, transmissao
     * comecando ou terminando, mensagem nova no bate-papo.
     *
     * `deafened` cala todos eles. "Silenciar a sala" e um pedido sobre a sala
     * inteira, e um bipe que atravessa o silencio pedido seria justamente o
     * tipo de som que faz a pessoa procurar onde desligar.
     */
    useRoomSounds(mesmaSala, session.messages, selfId, deafened, selfChannel, session.historyVersion)

    // O proprio nivel entra junto: o anel tem que acender pra voce tambem,
    // senao nao da pra saber se o microfone esta pegando.
    const speaking = useVoiceLevels(useMemo(() => [
        ...voiceTracks.map((track) => ({ id: track.peerId, stream: track.stream })),
        ...(session.voiceStream && selfId && !session.muted
            ? [{ id: selfId, stream: session.voiceStream }]
            : [])
    ], [voiceTracks, session.voiceStream, session.muted, selfId]))

    const { setAudioState, muted: sessionMuted } = session
    // Sincroniza o estado de mudo e fone (deafen) com o servidor para que todos na sala vejam
    useEffect(() => {
        setAudioState(sessionMuted, deafened)
    }, [sessionMuted, deafened, setAudioState])

    /**
     * Janela flutuante.
     *
     * Onde ha Document PiP (Chrome, Edge), o palco INTEIRO vai pra janelinha —
     * mosaico, etiquetas e tudo. Sem ele, sobra o PiP de video comum, que leva
     * so a transmissao em destaque e e melhor que nada.
     */
    const togglePip = useCallback(async () => {
        if (pip.pipWindow) {
            pip.close()
            return
        }
        if (pip.supported) {
            await pip.open()
            return
        }

        const video = stageRef.current?.querySelector('video')
        if (!video) return
        try {
            if (document.pictureInPictureElement) await document.exitPictureInPicture()
            else await video.requestPictureInPicture()
        } catch {
            // Navegador recusou (sem gesto, ou video ainda sem quadro).
        }
    }, [pip])

    const pipAvailable = pip.supported || videoPipSupported()

    /** Destacar uma tela liga o modo destaque; limpar volta pro mosaico. */
    const handleFocusChange = useCallback((id: string | null) => {
        setFocusedId(id)
        setMode(id ? 'destaque' : 'mosaico')
    }, [])

    /**
     * Liga ou desliga aquele tipo, sem tocar no outro: tela e camera sao
     * transmissoes independentes.
     */
    const toggleShare = useCallback((kind: ShareKind) => {
        if (sharingKinds.has(kind)) session.stopSharing(kind)
        else void session.startSharing(kind)
    }, [sharingKinds, session])

    /** Trocar de camera durante a transmissao reabre a captura no dispositivo novo. */
    const changeCamera = useCallback((deviceId: string) => {
        session.setCameraId(deviceId)
        if (sharingKinds.has('camera')) void session.startSharing('camera')
    }, [sharingKinds, session])

    /**
     * Manda uma transmissao sozinha pra janela. Clicar de novo na mesma traz
     * de volta; clicar em outra troca sem fechar a janela.
     */
    const pipOnly = useCallback(async (id: string) => {
        if (pip.pipWindow && pipTargetId === id) {
            setPipTargetId(null)
            pip.close()
            return
        }
        setPipTargetId(id)
        // Janela recusada (sem gesto, ou cancelada): o tile nao pode ficar
        // aceso como se estivesse nela.
        if (!pip.pipWindow && !(await pip.open())) setPipTargetId(null)
    }, [pip, pipTargetId])

    /**
     * A janela fechou — pelo X dela, pelo botao da barra de cima, por onde for:
     * a transmissao escolhida vai junto. Sem isto, o tile seguia com o icone
     * aceso, e o proximo clique na barra reabria so aquela tela em vez da sala.
     */
    useEffect(() => {
        if (!pip.pipWindow) setPipTargetId(null)
    }, [pip.pipWindow])

    /** A escolhida saiu do ar: a janela volta a mostrar o palco inteiro. */
    useEffect(() => {
        if (pipTargetId && !stageStreams.some((entry) => entry.id === pipTargetId)) {
            setPipTargetId(null)
        }
    }, [pipTargetId, stageStreams])

    /**
     * Tamanho medido de cada transmissao, por id de tile.
     *
     * Vive AQUI, e nao dentro do palco, porque a lista de participantes mostra
     * a mesma etiqueta: guardar a medida nos dois daria duas verdades sobre o
     * mesmo video, e elas divergiriam na hora em que mais importa — quando a
     * camada do simulcast troca.
     */
    const [measures, setMeasures] = useState<StreamMeasures>({})

    const reportMeasure = useCallback((id: string, measure: StreamMeasure) => {
        setMeasures((current) => {
            const atual = current[id]
            return atual
                && atual.width === measure.width
                && atual.height === measure.height
                && atual.fps === measure.fps
                ? current
                : { ...current, [id]: measure }
        })
    }, [])

    /**
     * A etiqueta pronta de cada pessoa, pra lista de participantes.
     *
     * Uma pessoa pode ter tela e camera ao mesmo tempo; a etiqueta acompanha a
     * tela, que e a que leva o "AO VIVO" ao lado. Sem transmissao de tela,
     * sobra a camera.
     */
    const qualityByPeer = useMemo(() => {
        const labels: Record<string, string> = {}
        for (const entry of stageStreams) {
            // A tela ganha da camera; sem tela, a camera fica.
            if (labels[entry.peerId] && entry.kind !== 'screen') continue
            const medida = measures[entry.id]
            if (!medida) continue
            const trackId = entry.stream?.getVideoTracks?.()[0]?.id
            const label = qualityLabel({
                width: medida.width,
                height: medida.height,
                fps: medida.fps,
                bitrate: (trackId ? session.bitrates[trackId] : undefined) ?? null
            })
            if (label) labels[entry.peerId] = label
        }
        return labels
    }, [stageStreams, measures, session.bitrates])

    // A background tab stops receiving, unless its sound is on or it plays in a floating window.
    const { setWatchedStreams } = session
    const receiving = useMemo(() => new Set([...watchedIds].filter((id) => (
        !pageAway || tileAudioOn[id] || !!pip.pipWindow
    ))), [watchedIds, pageAway, tileAudioOn, pip.pipWindow])
    useEffect(() => {
        setWatchedStreams(receiving)
    }, [receiving, setWatchedStreams])
    useEffect(() => () => setWatchedStreams(new Set()), [setWatchedStreams])

    const pipTarget = pipTargetId
        ? stageStreams.find((entry) => entry.id === pipTargetId) ?? null
        : null

    const stage = (
        <Stage
            streams={visibleStreams}
            onHide={toggleWatch}
            mode={mode}
            focusedId={focusedId}
            onFocusChange={handleFocusChange}
            onPip={pipAvailable ? (id) => void pipOnly(id) : undefined}
            audioOn={tileAudioOn}
            volumes={tileVolumes}
            onAudioChange={(id, on) => setTileAudioOn((atual) => ({ ...atual, [id]: on }))}
            onTileVolumeChange={(id, vol) => setTileVolumes((atual) => ({ ...atual, [id]: vol }))}
            pipId={pipTargetId}
            deafened={deafened}
            speaking={speaking}
            peerStates={session.peerStates}
            measures={measures}
            onMeasure={reportMeasure}
            bitrates={session.bitrates}
            statsEnabled={session.statsEnabled}
            receivePreference={session.receivePreference}
            onReceiveLimit={session.requestReceiveLimit}
            empty={availableStreams.length > 0 ? (
                <AvailableCards streams={availableStreams} thumbnails={session.thumbnails} onWatch={toggleWatch} onWatchAll={watchAll} />
            ) : (
                <EmptyStage
                    code={room.code}
                    canShare={canShare}
                    canUseCamera={canUseCamera}
                    onShare={() => toggleShare('screen')}
                    onCamera={() => toggleShare('camera')}
                />
            )}
        />
    )


    // Atalhos: M alterna a visualizacao, F tela cheia, Esc volta pro mosaico.
    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            if (target && ['INPUT', 'TEXTAREA'].includes(target.tagName)) return

            if (event.key === 'm' || event.key === 'M') {
                setMode((current) => (current === 'mosaico' ? 'destaque' : 'mosaico'))
            } else if (event.key === 'f' || event.key === 'F') {
                void toggleFullscreen()
            } else if (event.key === 'Escape' && mode === 'destaque') {
                handleFocusChange(null)
            }
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [mode, toggleFullscreen, handleFocusChange])

    const salvarSala = async (fields: RoomEditorValues) => {
        setErroEdicao(null)
        const resultado = await patchPermanentRoom(room.code, fields)
        if (!resultado.ok) {
            setErroEdicao(mensagemDeErro(resultado.error))
            return
        }
        // O nome novo chega sozinho pelo `room-update` do servidor, que avisa a
        // sala inteira — nao ha o que atualizar na mao aqui.
        setEditando(false)
    }

    return (
        <div ref={shellRef} className="flex h-full flex-col bg-background">
            <RoomTopbar
                brand={session.config.brand}
                code={room.code}
                roomName={room.name}
                onEditRoom={podeEditarSala ? () => setEditando(true) : undefined}
                participants={room.participants.length}
                status={session.status}
                locked={room.locked}
                isHost={!!self?.host}
                mode={mode}
                modeEnabled={visibleStreams.length > 1}
                isFullscreen={isFullscreen}
                pipAvailable={pipAvailable}
                pipOpen={!!pip.pipWindow}
                onTogglePip={() => void togglePip()}
                onModeChange={(next) => {
                    setMode(next)
                    if (next === 'destaque' && !focusedId) setFocusedId(visibleStreams[0]?.id ?? null)
                }}
                onToggleLock={() => session.setLocked(!room.locked)}
                onToggleFullscreen={() => void toggleFullscreen()}
                onOpenParticipants={() => setPainelAberto(true)}
            />

            <VoicePlayback tracks={voiceTracks} volumes={volumes} deafened={deafened} />

            <div className="flex min-h-0 flex-1">
                <RoomSidebar
                    onWatchScreen={(peerId) => setWatchedIds((atual) => new Set([...atual, `${peerId}:screen`]))}
                    code={room.code}
                    roomName={room.name}
                    speaking={speaking}
                    volumes={volumes}
                    qualityByPeer={qualityByPeer}
                    onVolumeChange={(peerId, volume) => setVolumes((current) => ({ ...current, [peerId]: volume }))}
                    participants={room.participants}
                    selfId={selfId}
                    isHost={!!self?.host}
                    isModerator={!!self && canModerate(self)}
                    bans={session.bans}
                    onRename={session.rename}
                    onKick={session.kick}
                    onSetAdmin={session.setAdmin}
                    onUnban={session.unban}
                    channels={room.channels}
                    selfChannel={selfChannel}
                    onJoinChannel={session.joinChannel}
                    onCreateChannel={session.createChannel}
                    onDeleteChannel={session.deleteChannel}
                    sharingKinds={sharingKinds}
                    canShareScreen={canShare}
                    canUseCamera={canUseCamera}
                    quality={session.quality}
                    resolution={session.resolution}
                    encoder={session.encoderChoice}
                    encoderNote={encoderNote}
                    cameraId={session.cameraId}
                    background={session.background}
                    effectLoading={session.effectLoading}
                    avatar={session.avatar}
                    onAvatarChange={session.setAvatar}
                    inVoice={session.inVoice}
                    muted={session.muted}
                    deafened={deafened}
                    screenHasAudio={session.screenHasAudio}
                    screenAudioMuted={session.screenAudioMuted}
                    onToggleScreenAudio={session.toggleScreenAudio}
                    voiceLoading={session.voiceLoading}
                    micId={session.micId}
                    noiseSuppression={session.noiseSuppression}
                    onToggleVoice={() => {
                        if (session.inVoice) session.stopVoice()
                        else void session.startVoice()
                    }}
                    onToggleMute={() => session.setMuted(!session.muted)}
                    onToggleDeafen={() => setDeafened((value) => !value)}
                    onMicChange={(deviceId) => void session.changeMicrophone(deviceId)}
                    onNoiseSuppressionChange={(enabled) => void session.setNoiseSuppression(enabled)}
                    onToggleShare={toggleShare}
                    onQualityChange={session.changeQuality}
                    onResolutionChange={session.changeResolution}
                    onEncoderChange={session.changeEncoder}
                    receivePreference={session.receivePreference}
                    onReceivePreferenceChange={session.config.mode === 'mesh' ? session.changeReceivePreference : undefined}
                    uploadLimit={session.uploadLimit}
                    onUploadLimitChange={session.changeUploadLimit}
                    onCameraChange={changeCamera}
                    onBackgroundChange={(effect) => void session.setBackground(effect)}
                    onLeave={session.leaveRoom}
                    onClose={() => setPainelAberto(false)}
                    className={cn(
                        'fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] bg-card shadow-2xl transition-transform duration-200',
                        'md:static md:z-auto md:w-64 md:max-w-none md:translate-x-0 md:bg-card/40 md:shadow-none md:transition-none',
                        painelAberto ? 'translate-x-0' : '-translate-x-full'
                    )}
                />

                {/* Fundo escuro da gaveta: fechar tocando fora e o gesto que
                    todo mundo ja tenta antes de procurar um botao. */}
                {painelAberto && (
                    <button
                        type="button"
                        aria-label="Fechar o painel da sala"
                        onClick={() => setPainelAberto(false)}
                        className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm md:hidden"
                    />
                )}

                {/* Coluna de flex, e nao bloco: a faixa de "Ocultas" entra e sai
                    daqui, e com o palco pedindo `h-full` ele reivindicava a
                    altura TODA do main por cima dela — transbordando por baixo,
                    atras da dock, exatamente a altura da faixa. Como item de
                    flex com `flex-1`, o palco passa a ficar com o que sobra. */}
                <main ref={palcoRef} className="relative flex min-w-0 flex-1 flex-col p-2 pb-24 sm:p-4 sm:pb-24">
                    {/* Com a janela flutuante aberta, o MESMO palco e renderizado
                        la dentro por portal: o React continua dono dele, entao
                        as streams seguem ligadas sem remontar nada. */}
                    {visibleStreams.length > 0 && (
                <AvailableStrip streams={availableStreams} thumbnails={session.thumbnails} onWatch={toggleWatch} onWatchAll={watchAll} />
            )}

            {pip.pipWindow ? (
                        <>
                            <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl border border-dashed border-border/70">
                                <div className="text-center">
                                    <PictureInPicture2 className="mx-auto h-8 w-8 text-muted-foreground" />
                                    <p className="mt-3 text-sm font-medium text-foreground">
                                        {pipTarget
                                            ? `${pipTarget.nickname} esta na janela flutuante`
                                            : 'A sala esta na janela flutuante'}
                                    </p>
                                    <MriButton
                                        variant="outline"
                                        size="sm"
                                        className="mt-3"
                                        onClick={pip.close}
                                    >
                                        Trazer de volta
                                    </MriButton>
                                </div>
                            </div>
                            {createPortal(
                                <div className="h-screen w-screen bg-background p-2">
                                    {pipTarget
                                        ? (
                                            <StreamTile
                                                entry={pipTarget}
                                                focused
                                                className="h-full w-full"
                                                deafened={deafened}
                                                onMeasure={reportMeasure}
                                                statsEnabled={session.statsEnabled}
                                                receivePreference={session.receivePreference}
                                                onReceiveLimit={session.requestReceiveLimit}
                                                bitrate={session.bitrates[
                                                    pipTarget.stream?.getVideoTracks?.()[0]?.id ?? ''
                                                ]}
                                                peerState={session.peerStates[pipTarget.peerId]}
                                                /*
                                                 * Este tile nasce fora do `Stage`, entao a
                                                 * preferencia de som precisa vir na mao — sem
                                                 * ela o botao de desmutar nao respondia aqui.
                                                 */
                                                audioOn={tileAudioOn[pipTarget.id] ?? false}
                                                volume={tileVolumes[pipTarget.id] ?? 1}
                                                onAudioChange={(id, on) => setTileAudioOn((atual) => ({ ...atual, [id]: on }))}
                                                onVolumeChange={(id, vol) => setTileVolumes((atual) => ({ ...atual, [id]: vol }))}
                                            />
                                        )
                                        : stage}
                                </div>,
                                pip.pipWindow.document.body
                            )}
                        </>
                    ) : (
                        <div ref={stageRef} className="min-h-0 flex-1">{stage}</div>
                    )}

                    {/* Some sozinha so quando ha o que assistir: com o palco vazio
                        ela e a unica coisa na tela, e sem ela quem entra pelo
                        celular (sidebar e `hidden md:flex`) fica sem botao de
                        sair ou de mudo. Com a janela flutuante aberta o palco
                        daqui e so um aviso — nao ha video pra ela cobrir. */}
                    <ControlDock
                        visible={dockVisivel}
                        sharingKinds={sharingKinds}
                        canShareScreen={canShare}
                        canUseCamera={canUseCamera}
                        quality={session.quality}
                    resolution={session.resolution}
                        encoder={session.encoderChoice}
                        encoderNote={encoderNote}
                        cameraId={session.cameraId}
                        background={session.background}
                        effectLoading={session.effectLoading}
                        inVoice={session.inVoice}
                        muted={session.muted}
                        deafened={deafened}
                        voiceLoading={session.voiceLoading}
                        micId={session.micId}
                        noiseSuppression={session.noiseSuppression}
                        onToggleVoice={() => {
                            if (session.inVoice) session.stopVoice()
                            else void session.startVoice()
                        }}
                        onToggleMute={() => session.setMuted(!session.muted)}
                        onToggleDeafen={() => setDeafened((value) => !value)}
                        onMicChange={(deviceId) => void session.changeMicrophone(deviceId)}
                        onNoiseSuppressionChange={(enabled) => void session.setNoiseSuppression(enabled)}
                        onToggleShare={toggleShare}
                        onChangeScreen={() => void session.startSharing('screen')}
                        screenHasAudio={session.screenHasAudio}
                        screenAudioMuted={session.screenAudioMuted}
                        onToggleScreenAudio={session.toggleScreenAudio}
                        onQualityChange={session.changeQuality}
                    onResolutionChange={session.changeResolution}
                        onEncoderChange={session.changeEncoder}
                        receivePreference={session.receivePreference}
                        onReceivePreferenceChange={session.config.mode === 'mesh' ? session.changeReceivePreference : undefined}
                        uploadLimit={session.uploadLimit}
                        onUploadLimitChange={session.changeUploadLimit}
                        onCameraChange={changeCamera}
                        onBackgroundChange={(effect) => void session.setBackground(effect)}
                        onLeave={session.leaveRoom}
                    />
                </main>

                {chatOpen && (
                    <ChatPanel
                        messages={session.messages}
                        participants={room.participants}
                        selfId={selfId}
                        channelName={canalAtual?.name}
                        onSend={session.sendChat}
                        onCollapse={() => setChatOpen(false)}
                        className="fixed inset-0 z-40 md:static md:z-auto"
                    />
                )}
            </div>

            {/*
              * Com o painel fechado, a unica porta de volta. Encostado na borda
              * direita e fino de proposito: o ponto de encolher e dar a tela
              * pro video, entao o que sobra nao pode roubar espaco.
              */}
            {!chatOpen && (
                <button
                    type="button"
                    onClick={() => setChatOpen(true)}
                    title="Abrir o bate-papo"
                    aria-label={naoLidas > 0 ? `Abrir o bate-papo (${naoLidas} novas)` : 'Abrir o bate-papo'}
                    className="absolute right-0 top-1/2 flex -translate-y-1/2 flex-col items-center gap-1 rounded-l-lg border border-r-0 border-border/70 bg-card/90 px-1.5 py-3 text-muted-foreground shadow-lg backdrop-blur transition-colors hover:text-foreground"
                >
                    <MessageSquare className="h-4 w-4" />
                    {naoLidas > 0 && (
                        <span className="min-w-[16px] rounded-full bg-primary px-1 text-[9px] font-bold leading-4 text-primary-foreground">
                            {naoLidas > 99 ? '99+' : naoLidas}
                        </span>
                    )}
                </button>
            )}

            {editando && room.name && (
                <div className="absolute inset-0 z-30 flex items-start justify-center bg-black/60 p-4 pt-20 backdrop-blur-sm">
                    <div className="w-full max-w-sm">
                        <RoomEditor
                            room={{ code: room.code, name: room.name, requiresPin: false }}
                            /*
                             * Sempre: quem edita daqui esta DENTRO da sala, e
                             * trocar o endereco deixaria a sala viva num codigo
                             * que ninguem mais alcanca. O servidor recusa do
                             * mesmo jeito; o botao desabilitado explica antes.
                             */
                            emUso
                            onSave={(fields) => void salvarSala(fields)}
                            onCancel={() => {
                                setEditando(false)
                                setErroEdicao(null)
                            }}
                        />
                        {erroEdicao && (
                            <p role="alert" className="mt-2 rounded-lg border border-destructive/40 bg-destructive/15 px-3 py-2 text-xs text-destructive">
                                {erroEdicao}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {session.error && (
                <div className="pointer-events-none absolute inset-x-0 top-16 flex justify-center px-4">
                    <p
                        role="alert"
                        className={cn(
                            'pointer-events-auto max-w-lg cursor-pointer rounded-lg border px-4 py-2 text-sm backdrop-blur',
                            session.error.severity === 'aviso'
                                ? 'border-amber-400/40 bg-amber-400/15 text-amber-200'
                                : 'border-destructive/40 bg-destructive/15 text-destructive'
                        )}
                        title="Clique pra dispensar"
                        onClick={session.clearError}
                    >
                        {session.error.message}
                    </p>
                </div>
            )}
        </div>
    )
}
